<script setup lang="ts">
import type { ModerationStatus } from '@wilson/shared'
import { onMounted, ref } from 'vue'
import { api, jsonBody } from '../../services/api'

interface CommentRow {
  id: string
  bottleId: string
  bottleName: string
  quoteName: string
  message: string
  publicLocation: string | null
  createdAt: string
  isVisible: boolean
  moderationStatus: ModerationStatus
  reported: boolean
}
const items = ref<CommentRow[]>([])
const total = ref(0)
const filters = ref({ from: '', to: '', moderation: '', visibility: '', reported: '' })
const error = ref('')
const query = () =>
  new URLSearchParams(
    Object.entries(filters.value)
      .filter(([, value]) => value)
      .map(([key, value]) => [
        key,
        key === 'from' || key === 'to' ? new Date(value).toISOString() : value,
      ]),
  ).toString()
async function load() {
  try {
    const data = await api<{ items: CommentRow[]; total: number }>(`/admin/comments?${query()}`)
    items.value = data.items
    total.value = data.total
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Comments could not be loaded.'
  }
}
async function update(
  item: CommentRow,
  changes: { isVisible?: boolean; moderationStatus?: ModerationStatus },
) {
  await api(`/admin/comments/${item.id}`, { method: 'PATCH', ...jsonBody(changes) })
  await load()
}
function exportUrl(format: string) {
  return `/api/v1/admin/comments/export?format=${format}&${query()}`
}
onMounted(load)
</script>

<template>
  <header class="admin-heading">
    <div>
      <p class="eyebrow">Anonymous traveller notes</p>
      <h1>Comments</h1>
      <p>Review and export messages without exposing private coordinates or finder capabilities.</p>
    </div>
    <div class="button-row">
      <a class="button secondary" :href="exportUrl('csv')">Export CSV</a
      ><a class="button secondary" :href="exportUrl('json')">Export JSON</a>
    </div>
  </header>
  <p v-if="error" class="form-error banner">{{ error }}</p>
  <section class="admin-card filter-bar">
    <label>From<input v-model="filters.from" type="datetime-local" @change="load" /></label
    ><label>To<input v-model="filters.to" type="datetime-local" @change="load" /></label
    ><label
      >Moderation<select v-model="filters.moderation" @change="load">
        <option value="">All</option>
        <option value="unreviewed">Unreviewed</option>
        <option value="safe">Safe</option>
        <option value="review">Review</option>
        <option value="inappropriate">Inappropriate</option>
      </select></label
    ><label
      >Visibility<select v-model="filters.visibility" @change="load">
        <option value="">All</option>
        <option value="visible">Visible</option>
        <option value="hidden">Hidden</option>
      </select></label
    ><label
      >Reported<select v-model="filters.reported" @change="load">
        <option value="">All</option>
        <option value="true">Reported</option>
        <option value="false">Not reported</option>
      </select></label
    >
  </section>
  <section class="comment-admin-list">
    <article v-for="item in items" :key="item.id" class="admin-card comment-row">
      <header>
        <div>
          <RouterLink :to="`/admin/bottles/${item.bottleId}`">{{ item.bottleName }}</RouterLink
          ><span>· {{ item.quoteName }}</span>
        </div>
        <time>{{ new Date(item.createdAt).toLocaleString() }}</time>
      </header>
      <p>{{ item.message }}</p>
      <footer>
        <div>
          <span class="status-pill" :class="item.moderationStatus">{{ item.moderationStatus }}</span
          ><span v-if="item.reported" class="status-pill warning">Reported</span
          ><span v-if="item.publicLocation">{{ item.publicLocation }}</span>
        </div>
        <div class="row-actions">
          <select
            :value="item.moderationStatus"
            @change="
              update(item, {
                moderationStatus: ($event.target as HTMLSelectElement).value as ModerationStatus,
              })
            "
          >
            <option value="unreviewed">Unreviewed</option>
            <option value="safe">Safe</option>
            <option value="review">Review</option>
            <option value="inappropriate">Inappropriate</option></select
          ><button @click="update(item, { isVisible: !item.isVisible })">
            {{ item.isVisible ? 'Hide' : 'Restore' }}
          </button>
        </div>
      </footer>
    </article>
    <p v-if="!items.length" class="empty-copy">No comments match these filters.</p>
  </section>
  <p class="list-total">{{ total }} total</p>
</template>
